import React, { useCallback, useMemo, useState } from 'react'
import {
  CellClassParams,
  CellValueChangedEvent,
  ColDef,
  ICellRendererParams,
} from '@ag-grid-community/core'
import { Switch } from '@surecompanies/core_components'
import { message } from 'antd'
import ReviewRecordsBase from '../components/CensusReviewRecordsBase'
import { CensusFileRowTypeEnum } from '../../api/enums/census-file-row-type.enum'
import { updateCensusFileRows } from '../../api/census-enrollment-platform-api-client'
import { useMarkRowDirtyErrors } from '../../hooks/use-mark-row-dirty-errors'
import { CensusFileRowGridData } from '../../types/census-file-row-grid-data'
import { CensusFileRowStatusEnum } from '../../api/enums/census-file-row-status.enum'
import { CensusFileRowDto } from '../../api/dto/census-file-row.dto'
import { mapGridDataToCensusFileRowDto } from '../../utils/map-grid-data-to-census-file-row-dto'
import InlineEditActionButtons from '../../components/InlineEditActionButtons'
import RejectSelectedButton from '../components/RejectSelectedButton'
import { LoadingOverlay } from '../../components/LoadingOverlay/LoadingOverlay'
import ToggleEditModeButton from '../../components/ToggleEditModeButton'
import { errorColumnStyling } from '../../utils/error-column-styling'
import { configureEditField } from '../../utils/configure-edit-field'
import { CensusManagementPageProps } from '../../types/census-management-page-props'
import { PatchActionEnum } from '../../api/enums/patch-action.enum'
import { ErrorTooltipRenderer } from '../../components/ErrorTooltipRenderer'
import CensusReviewCsvExportButton from '../components/CensusReviewCsvExportButton'
import { CreateCensusFieldIgnoreRuleRequestDto } from '../../api/dto/census-field-ignore-rule.dto'
import './CensusReview.scss'

const makeIgnoreKey = (rowId: number | undefined, field: string) =>
  `${rowId ?? ''}:${field}`

// Column to display error keys with tooltips
const errorColumn: ColDef<CensusFileRowGridData> = {
  headerName: 'Errors',
  field: 'errors',
  sortable: true,
  width: 200,
  pinned: 'left',
  cellStyle: { color: 'red', fontWeight: 'bold' },
  cellRenderer: ErrorTooltipRenderer,
  // Format errors object for export (CSV/Excel), excluding ignored fields
  valueGetter: (params) => {
    const errors = params.data?.errors
    if (!errors || typeof errors !== 'object') return ''

    const ignoredFields = params.data?.updates?.ignoredFields ?? {}
    const locallyIgnoredErrors =
      (params.context?.locallyIgnoredErrors as Record<string, 'pending'>) ?? {}
    const rowId = params.data?.id

    return Object.entries(errors)
      .filter(([field]) => {
        if (ignoredFields[field]) return false
        if (locallyIgnoredErrors[makeIgnoreKey(rowId, field)] === 'pending') {
          return false
        }
        return true
      })
      .map(([field, error]: [string, any]) => {
        const errorMessage = error?.errorMessage || error
        return `${field}: ${errorMessage}`
      })
      .join('; ')
  },
}

// Show errors on page
const CensusReviewRecordsError = (props: CensusManagementPageProps) => {
  const { markRowDirty, dirtyRows, cleanAllRows, undoLastEdit, editCount } =
    useMarkRowDirtyErrors()
  const [loading, setLoading] = useState(false)
  const [mode, setMode] = useState<'edit' | 'submit'>('submit')
  const { importPublicKey: publicKey } = props.routeParams

  const [locallyIgnoredErrors, setLocallyIgnoredErrors] = useState<
    Record<string, 'pending'>
  >({})
  const [pendingIgnoreCreates, setPendingIgnoreCreates] = useState<
    Array<
      CreateCensusFieldIgnoreRuleRequestDto & {
        censusRowId: number
        displayEmployeeId: string
        displayName: string
        gridRow: CensusFileRowGridData
      }
    >
  >([])

  // Tracks cell edits and ignore toggles in LIFO order so undo always
  // reverses the most recent action regardless of type.
  type UndoEntry = { type: 'cell' } | { type: 'ignore'; key: string }
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([])

  const cleanAllRowsAndStack = useCallback(() => {
    cleanAllRows()
    setUndoStack([])
  }, [cleanAllRows])

  const handleUndoLast = () => {
    if (undoStack.length === 0) return
    const last = undoStack[undoStack.length - 1]
    if (last.type === 'ignore') {
      const entry = pendingIgnoreCreates.find(
        (p) => makeIgnoreKey(p.censusRowId, p.fieldName) === last.key
      )
      if (!entry) return
      handleUnignoreError(entry.gridRow, entry.fieldName)
    } else {
      undoLastEdit()
    }
    setUndoStack((prev) => prev.slice(0, -1))
  }

  const handleIgnoreError = useCallback(
    (row: CensusFileRowGridData, fieldName: string): boolean => {
      const employeePublicKey = row.updates?.employeePublicKey
      if (!employeePublicKey) {
        message.error('Missing employee data to ignore this field.')
        return false
      }
      const key = makeIgnoreKey(row.id, fieldName)
      const fieldValue = row[fieldName]
      // The current DB value for this field, provided by the backend. It's the
      // value that will be kept when the incoming file value is ignored.
      // `null`/`undefined` mean the backend didn't provide it (skip so we don't
      // send the literal "null"); `''` is a valid value (the DB value is empty).
      const errorLiveValue = row.errors?.[fieldName]?.liveValue
      setLocallyIgnoredErrors((prev) => ({ ...prev, [key]: 'pending' }))
      setPendingIgnoreCreates((prev) => {
        const filtered = prev.filter(
          (p) =>
            !(
              p.employeePublicKey === employeePublicKey &&
              p.fieldName === fieldName
            )
        )
        return [
          ...filtered,
          {
            employeePublicKey,
            fieldName,
            ignoredNewValue: (fieldValue ?? '') as string | number | boolean,
            liveValue:
              errorLiveValue !== undefined && errorLiveValue !== null
                ? String(errorLiveValue)
                : undefined,
            censusRowId: row.id,
            displayEmployeeId: String(row.CompanyEmployeeId ?? '-'),
            displayName:
              `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim() || '-',
            gridRow: row,
          },
        ]
      })
      return true
    },
    []
  )

  const handleUnignoreError = useCallback(
    (row: CensusFileRowGridData, fieldName: string) => {
      const employeePublicKey = row.updates?.employeePublicKey
      const key = makeIgnoreKey(row.id, fieldName)
      setLocallyIgnoredErrors((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
      if (employeePublicKey) {
        setPendingIgnoreCreates((prev) =>
          prev.filter(
            (p) =>
              !(
                p.employeePublicKey === employeePublicKey &&
                p.fieldName === fieldName
              )
          )
        )
      }
    },
    []
  )

  const handleToggleIgnoreError = useCallback(
    (row: CensusFileRowGridData, fieldName: string, shouldIgnore: boolean) => {
      if (shouldIgnore) {
        const ignored = handleIgnoreError(row, fieldName)
        if (ignored) {
          setUndoStack((prev) => [
            ...prev,
            { type: 'ignore', key: makeIgnoreKey(row.id, fieldName) },
          ])
        }
      } else {
        handleUnignoreError(row, fieldName)
        const key = makeIgnoreKey(row.id, fieldName)
        setUndoStack((prev) => {
          const idx = prev.findIndex(
            (e) => e.type === 'ignore' && e.key === key
          )
          return idx === -1
            ? prev
            : [...prev.slice(0, idx), ...prev.slice(idx + 1)]
        })
      }
    },
    [handleIgnoreError, handleUnignoreError]
  )

  // Passed to CensusReviewRecordsBase → AG Grid context so cell renderers
  // always read the latest ignore state without a column-def rebuild.
  const gridContext = useMemo(
    () => ({
      mode,
      locallyIgnoredErrors,
      handleToggleIgnoreError,
    }),
    [mode, locallyIgnoredErrors, handleToggleIgnoreError]
  )

  // Update rows with new data; also ship any pending ignore rules
  const submitCorrections = async (rowsToSubmit: CensusFileRowGridData[]) => {
    // Merge dirty rows + ignored-only rows (dedup by id)
    const dirtyRowIds = new Set(rowsToSubmit.map((r) => r.id))
    const ignoredOnlyRows = Array.from(
      new Map(
        pendingIgnoreCreates
          .filter((p) => !dirtyRowIds.has(p.censusRowId))
          .map((p) => [p.censusRowId, p.gridRow])
      ).values()
    )
    const allRowsToSubmit = [...rowsToSubmit, ...ignoredOnlyRows]
    const updatedRows: CensusFileRowDto[] = allRowsToSubmit.map((row) =>
      mapGridDataToCensusFileRowDto(row)
    )

    // Send all pending ignores regardless of which rows are dirty —
    // ignore and edit operate on independent rows
    const ignoreRulesToSend = pendingIgnoreCreates.map(
      ({ employeePublicKey, fieldName, ignoredNewValue, liveValue }) => ({
        employeePublicKey,
        fieldName,
        ignoredNewValue,
        liveValue,
      })
    )

    try {
      await updateCensusFileRows(
        updatedRows,
        publicKey,
        PatchActionEnum.UPDATE_RECORDS,
        ignoreRulesToSend.length
          ? { ignoreRules: ignoreRulesToSend }
          : undefined
      )

      // Only switch to submit mode and clear state after a successful save
      setMode('submit')
      if (pendingIgnoreCreates.length > 0) {
        setPendingIgnoreCreates([])
        setLocallyIgnoredErrors({})
        setUndoStack([])
      }
    } catch (error: any) {
      if (error?.response?.status === 422) {
        message.error(
          'One or more ignore rules have empty values — empty values are not allowed. Please review and try again.'
        )
      } else {
        message.error('Failed to save edits. Please try again.')
      }
      throw error
    }
  }

  const handleCellValueChanged = useCallback(
    (params: CellValueChangedEvent<CensusFileRowGridData>) => {
      const { status, removedEdits } = markRowDirty(params)
      // A duplicate grid event recorded nothing, so the undo stack must be left
      // alone - dropping an entry here would strand the edit with no way to
      // undo it.
      if (status === 'duplicate') return
      if (status === 'appended') {
        setUndoStack((prev) => [...prev, { type: 'cell' }])
        return
      }
      // Returning a field to its starting value drops every edit it had, so
      // drop the same number of cell entries here to keep this stack in step
      // with the hook's history.
      if (removedEdits === 0) return
      setUndoStack((prev) => {
        let left = removedEdits
        const kept: UndoEntry[] = []
        for (let i = prev.length - 1; i >= 0; i -= 1) {
          if (left > 0 && prev[i].type === 'cell') {
            left -= 1
            continue
          }
          kept.unshift(prev[i])
        }
        return kept
      })
    },
    []
  )

  const modifyColDefs = useCallback(
    (
      baseColDefs: ColDef<CensusFileRowGridData>[]
    ): ColDef<CensusFileRowGridData>[] => {
      const showIgnoreControls = mode === 'edit'

      const errorsColDef: ColDef<CensusFileRowGridData> = showIgnoreControls
        ? {
            ...errorColumn,
            width: 250,
            autoHeight: true,
            cellRenderer: (params: ICellRendererParams) => {
              const row = params.data as CensusFileRowGridData
              const errors = row?.errors
              if (!errors || Object.keys(errors).length === 0) return null
              const ctx = params.context ?? {}

              const canIgnore = !!row?.updates?.employeePublicKey

              return (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    padding: '4px 0',
                  }}
                >
                  {Object.keys(errors).map((field) => {
                    const ignoreKey = makeIgnoreKey(row?.id, field)
                    const isLocallyIgnored =
                      ctx.locallyIgnoredErrors?.[ignoreKey] === 'pending'
                    const isAlreadyIgnored =
                      !!row.updates?.ignoredFields?.[field]
                    const isIgnored = isLocallyIgnored || isAlreadyIgnored
                    return (
                      <div
                        key={field}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            color: isIgnored ? '#ad8b00' : 'red',
                            fontWeight: 'bold',
                            fontSize: 12,
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {field}
                        </span>
                        {canIgnore && (
                          <span className="census-ignore-toggle">
                            <span className="census-ignore-toggle__label">
                              Ignore
                            </span>
                            <Switch
                              size="small"
                              checked={isIgnored}
                              disabled={isAlreadyIgnored}
                              onChange={(checked) =>
                                ctx.handleToggleIgnoreError?.(
                                  row,
                                  field,
                                  checked
                                )
                              }
                            />
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            },
          }
        : errorColumn

      return [
        {
          headerCheckboxSelection: mode === 'submit',
          checkboxSelection: mode === 'submit',
          width: 50,
          pinned: 'left',
        },
        errorsColDef,
        ...baseColDefs.map((col) => {
          const errorStyling = errorColumnStyling(col.field, mode)

          return {
            ...col,
            ...configureEditField(col.field, mode),
            ...errorStyling,
            editable: (params) => {
              const field = col.field ?? ''
              const ignoreKey = makeIgnoreKey(params.data?.id, field)
              const isLocallyIgnored =
                params.context?.locallyIgnoredErrors?.[ignoreKey] === 'pending'
              const isAlreadyIgnored =
                !!params.data?.updates?.ignoredFields?.[field]
              return mode === 'edit' && !isLocallyIgnored && !isAlreadyIgnored
            },
            cellStyle: (params: CellClassParams<CensusFileRowGridData>) => {
              const field = col.field ?? ''
              const ignoreKey = makeIgnoreKey(params.data?.id, field)
              const isLocallyIgnored =
                params.context?.locallyIgnoredErrors?.[ignoreKey] === 'pending'
              const isAlreadyIgnored =
                !!params.data?.updates?.ignoredFields?.[field]
              if (isLocallyIgnored || isAlreadyIgnored) {
                return {
                  backgroundColor: '#fff7e6',
                  color: '#ad8b00',
                  opacity: 0.75,
                }
              }
              if (typeof errorStyling.cellStyle === 'function') {
                return errorStyling.cellStyle(params)
              }
              return errorStyling.cellStyle ?? undefined
            },
          }
        }),
      ]
    },
    [mode]
  )

  return (
    <>
      <LoadingOverlay isLoading={loading} message="Loading..." />
      <ReviewRecordsBase
        pageTitle={'Review New & Updated Records'}
        tabKey={CensusFileRowTypeEnum.ERROR}
        {...props}
        rowQueryParams={{
          censusFileRowStatuses: [CensusFileRowStatusEnum.IN_REVIEW],
          censusRowType: CensusFileRowTypeEnum.ERROR,
        }}
        modifyColDefs={modifyColDefs}
        gridContext={gridContext}
        actionButton={({ selectedRows, handleSubmit }) => {
          return (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <ToggleEditModeButton
                dirtyRows={dirtyRows}
                selectedRows={selectedRows}
                mode={mode}
                setMode={setMode}
                pendingEditsCount={editCount + pendingIgnoreCreates.length}
              />
              {mode === 'edit' ? (
                <InlineEditActionButtons
                  undoLastEdit={handleUndoLast}
                  editCount={editCount + pendingIgnoreCreates.length}
                  dirtyRows={dirtyRows}
                  handleSubmit={handleSubmit}
                  cleanAllRows={cleanAllRowsAndStack}
                  submitCorrections={submitCorrections}
                  updateMode={false}
                  setLoading={setLoading}
                  pendingIgnoreCreates={pendingIgnoreCreates}
                />
              ) : (
                <>
                  <CensusReviewCsvExportButton
                    publicKey={publicKey}
                    exportType={CensusFileRowTypeEnum.ERROR}
                  />
                  <RejectSelectedButton
                    publicKey={publicKey}
                    selectedRows={selectedRows}
                    handleSubmit={() => {
                      setMode('submit')
                      handleSubmit()
                    }}
                    cleanAllRows={cleanAllRows}
                    setLoading={setLoading}
                  />
                </>
              )}
            </div>
          )
        }}
        handleCellValueChanged={handleCellValueChanged}
      />
    </>
  )
}

export default CensusReviewRecordsError
